import React from "react";

const Footer = () => {
  const year = new Date().getFullYear();

  const SocialLink = ({ href, label, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label={label}
      title={label}
      className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/5 text-zinc-200 ring-1 ring-white/10 transition hover:bg-white/10 hover:text-white"
    >
      {children}
    </a>
  );

  const FooterLink = ({ href, children }) => (
    <a href={href} className="text-sm text-zinc-400 transition hover:text-white">
      {children}
    </a>
  );

  const Badge = ({ children }) => (
    <span className="inline-flex items-center rounded-lg bg-white/5 px-2.5 py-1 text-xs font-medium text-zinc-200 ring-1 ring-white/10">
      {children}
    </span>
  );

  // ✅ Tile untuk logo pembayaran (online)
  const PaymentLogo = ({ label, src, className = "" }) => (
    <div
      className="inline-flex h-10 items-center justify-center rounded-xl bg-white px-3 ring-1 ring-white/10"
      title={label}
      aria-label={label}
    >
      <img
        src={src}
        alt={label}
        loading="lazy"
        referrerPolicy="no-referrer"
        className={`h-6 w-auto ${className}`}
      />
    </div>
  );

  // ✅ Sumber logo online
  const PAYMENT_LOGOS = [
    {
      label: "VISA",
      // Simple Icons (SVG) via unpkg
      src: "https://unpkg.com/simple-icons@15.16.0/icons/visa.svg",
    },
    {
      label: "Mastercard",
      src: "https://unpkg.com/simple-icons@15.16.0/icons/mastercard.svg",
    },
    {
      label: "PayPal",
      src: "https://unpkg.com/simple-icons@15.16.0/icons/paypal.svg",
    },
    {
      label: "GoPay",
      // SeekLogo (PNG)
      src: "https://images.seeklogo.com/logo-png/36/1/gopay-logo-png_seeklogo-369813.png",
    },
    {
      label: "OVO",
      src: "https://images.seeklogo.com/logo-png/64/1/ovo-logo-png_seeklogo-644219.png",
    },
    {
      label: "DANA",
      src: "https://images.seeklogo.com/logo-png/39/1/dana-e-wallet-app-logo-png_seeklogo-399948.png",
    },
  ];

  return (
    <footer className="border-t border-white/10 bg-zinc-950 text-zinc-200">
      {/* Top */}
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-12">
          {/* Brand + value props */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-2xl bg-white/10 ring-1 ring-white/10">
                <svg
                  viewBox="0 0 24 24"
                  className="h-5 w-5"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M12 2l8 4v6c0 5-3.5 9.4-8 10-4.5-.6-8-5-8-10V6l8-4zm0 2.2L6 7v5c0 3.9 2.6 7.4 6 8 3.4-.6 6-4.1 6-8V7l-6-2.8z" />
                </svg>
              </div>
              <div>
                <p className="text-base font-extrabold text-white">Hyperstore</p>
                <p className="text-xs text-zinc-400">Modern goods. Fast delivery.</p>
              </div>
            </div>

            <p className="mt-4 max-w-xl text-sm leading-relaxed text-zinc-400">
              Belanja nyaman dengan produk pilihan, pembayaran aman, dan pengiriman cepat.
              Kami fokus bikin pengalaman e-commerce yang simpel, cepat, dan terpercaya.
            </p>

            <div className="mt-5 flex flex-wrap gap-2">
              <Badge>Free Returns</Badge>
              <Badge>Secure Payment</Badge>
              <Badge>24/7 Support</Badge>
              <Badge>Fast Delivery</Badge>
            </div>

            {/* Newsletter */}
            <div className="mt-8 rounded-2xl bg-white/5 p-4 ring-1 ring-white/10">
              <p className="text-sm font-semibold text-white">Dapatkan promo terbaru</p>
              <p className="mt-1 text-sm text-zinc-400">
                Subscribe newsletter untuk diskon & update produk.
              </p>

              <form
                onSubmit={(e) => e.preventDefault()}
                className="mt-4 flex flex-col gap-3 sm:flex-row"
              >
                <label className="sr-only" htmlFor="email">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  placeholder="Email kamu"
                  className="h-11 w-full rounded-xl bg-zinc-950 px-4 text-sm text-zinc-100 ring-1 ring-white/10 placeholder:text-zinc-500 outline-none focus:ring-2 focus:ring-white/20"
                />
                <button
                  type="submit"
                  className="h-11 shrink-0 rounded-xl bg-white px-5 text-sm font-bold text-zinc-950 transition hover:bg-zinc-200"
                >
                  Subscribe
                </button>
              </form>

              <p className="mt-3 text-xs text-zinc-500">
                Dengan subscribe, kamu setuju menerima email promo. Bisa unsubscribe kapan saja.
              </p>
            </div>
          </div>

          {/* Links */}
          <div className="lg:col-span-7">
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <p className="text-sm font-semibold text-white">Shop</p>
                <ul className="mt-4 space-y-3">
                  <li><FooterLink href="/product">All Products</FooterLink></li>
                  <li><FooterLink href="/new">New Arrivals</FooterLink></li>
                  <li><FooterLink href="/best-seller">Best Seller</FooterLink></li>
                  <li><FooterLink href="/sale">Sale</FooterLink></li>
                </ul>
              </div>

              <div>
                <p className="text-sm font-semibold text-white">Company</p>
                <ul className="mt-4 space-y-3">
                  <li><FooterLink href="/about">About</FooterLink></li>
                  <li><FooterLink href="/careers">Careers</FooterLink></li>
                  <li><FooterLink href="/contact">Contact</FooterLink></li>
                  <li><FooterLink href="/stores">Store Locator</FooterLink></li>
                </ul>
              </div>

              <div>
                <p className="text-sm font-semibold text-white">Support</p>
                <ul className="mt-4 space-y-3">
                  <li><FooterLink href="/help">Help Center</FooterLink></li>
                  <li><FooterLink href="/shipping">Shipping</FooterLink></li>
                  <li><FooterLink href="/returns">Returns</FooterLink></li>
                  <li><FooterLink href="/faq">FAQ</FooterLink></li>
                </ul>
              </div>
            </div>

            {/* Contact + socials + payments */}
            <div className="mt-10 grid gap-6 rounded-2xl bg-white/5 p-5 ring-1 ring-white/10 sm:grid-cols-2">
              <div>
                <p className="text-sm font-semibold text-white">Contact</p>
                <div className="mt-3 space-y-2 text-sm text-zinc-400">
                  <p className="flex items-center gap-2">
                    <span className="text-zinc-300">Email:</span>
                    <a className="hover:text-white" href="mailto:support@hyperstore.com">
                      support@hyperstore.com
                    </a>
                  </p>
                  <p className="flex items-center gap-2">
                    <span className="text-zinc-300">WhatsApp:</span>
                    <a
                      className="hover:text-white"
                      href="https://wa.me/6281234567890"
                      target="_blank"
                      rel="noreferrer"
                    >
                      +62 812-3456-7890
                    </a>
                  </p>
                  <p className="text-xs text-zinc-500">Jam operasional: 09.00–18.00 WIB</p>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <Badge>COD</Badge>
                  <Badge>Bank Transfer</Badge>
                  <Badge>E-Wallet</Badge>
                </div>
              </div>

              <div>
                <p className="text-sm font-semibold text-white">Follow us</p>
                <div className="mt-3 flex items-center gap-3">
                  {/* Instagram */}
                  <SocialLink href="https://instagram.com" label="Instagram">
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
                      <path d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5zm10 2H7a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3zm-5 4.5A5.5 5.5 0 1 1 6.5 12 5.5 5.5 0 0 1 12 8.5zm0 2A3.5 3.5 0 1 0 15.5 12 3.5 3.5 0 0 0 12 10.5zM18 7a1 1 0 1 1-1-1 1 1 0 0 1 1 1z" />
                    </svg>
                  </SocialLink>

                  {/* Twitter/X */}
                  <SocialLink href="https://x.com" label="X">
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
                      <path d="M18.9 2H22l-6.8 7.8L23 22h-6.3l-4.9-6.3L6.3 22H2l7.3-8.4L1 2h6.5l4.5 5.8L18.9 2zm-1.1 18h1.7L7.1 3.9H5.3L17.8 20z" />
                    </svg>
                  </SocialLink>

                  {/* YouTube */}
                  <SocialLink href="https://youtube.com" label="YouTube">
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
                      <path d="M21.6 7.2a3 3 0 0 0-2.1-2.1C17.7 4.6 12 4.6 12 4.6s-5.7 0-7.5.5A3 3 0 0 0 2.4 7.2 31.2 31.2 0 0 0 2 12a31.2 31.2 0 0 0 .4 4.8 3 3 0 0 0 2.1 2.1c1.8.5 7.5.5 7.5.5s5.7 0 7.5-.5a3 3 0 0 0 2.1-2.1A31.2 31.2 0 0 0 22 12a31.2 31.2 0 0 0-.4-4.8zM10 15.5v-7l6 3.5-6 3.5z" />
                    </svg>
                  </SocialLink>

                  {/* GitHub */}
                  <SocialLink href="https://github.com" label="GitHub">
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
                      <path d="M12 2C6.48 2 2 6.58 2 12.26c0 4.5 2.87 8.32 6.84 9.67.5.1.68-.22.68-.48 0-.24-.01-.87-.01-1.7-2.78.62-3.37-1.38-3.37-1.38-.46-1.2-1.11-1.52-1.11-1.52-.9-.63.07-.62.07-.62 1 .07 1.53 1.06 1.53 1.06.9 1.58 2.36 1.12 2.94.86.09-.67.35-1.12.63-1.38-2.22-.26-4.56-1.14-4.56-5.09 0-1.12.39-2.03 1.03-2.74-.1-.26-.45-1.3.1-2.7 0 0 .84-.28 2.75 1.05A9.2 9.2 0 0 1 12 7.32c.85 0 1.7.12 2.5.35 1.9-1.33 2.74-1.05 2.74-1.05.55 1.4.2 2.44.1 2.7.64.71 1.03 1.62 1.03 2.74 0 3.96-2.34 4.83-4.58 5.08.36.32.68.96.68 1.93 0 1.39-.01 2.51-.01 2.85 0 .26.18.59.69.48A10.04 10.04 0 0 0 22 12.26C22 6.58 17.52 2 12 2z" />
                    </svg>
                  </SocialLink>
                </div>

                <p className="mt-5 text-sm font-semibold text-white">We accept</p>

                {/* ✅ Payments as icons */}
                <div className="mt-3 flex flex-wrap gap-2">
                  {PAYMENT_LOGOS.map((p) => (
                    <PaymentLogo key={p.label} label={p.label} src={p.src} />
                  ))}
                </div>

                <p className="mt-2 text-xs text-zinc-500">
                  * Logo ditampilkan via sumber online.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-zinc-400">
              © {year} <span className="font-semibold text-zinc-200">Hyperstore</span>. All rights reserved.
            </p>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
              <a href="/privacy" className="text-zinc-400 hover:text-white">
                Privacy
              </a>
              <a href="/terms" className="text-zinc-400 hover:text-white">
                Terms
              </a>
              <a href="/cookies" className="text-zinc-400 hover:text-white">
                Cookies
              </a>
              <span className="hidden sm:inline text-zinc-600">•</span>
              <p className="text-zinc-500">
                Made with <span className="mx-1">❤️</span> by{" "}
                <a
                  href="https://ganeshahost.my.id/"
                  target="_blank"
                  rel="noreferrer"
                  className="font-extrabold text-white underline decoration-white/30 underline-offset-4 hover:decoration-white"
                >
                  Ganeshahost
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
